import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/toaster";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import Doctors from "@/pages/admin/Doctors";
import Patients from "@/pages/admin/Patients";
import Appointments from "@/pages/admin/Appointments";
import Staff from "@/pages/admin/Staff";
import Departments from "@/pages/admin/Departments";
import Vendors from "@/pages/admin/Vendors";
import Inventory from "@/pages/admin/Inventory";
import Lab from "@/pages/admin/Lab";
import Billing from "@/pages/admin/Billing";
import Settings from "@/pages/admin/Settings";
import AdminCalendar from "@/pages/admin/Calendar";
import Analytics from "@/pages/admin/Analytics";
import Insurance from "@/pages/admin/Insurance";
import Notifications from "@/pages/admin/Notifications";
import Schedule from "@/pages/Schedule";
import DoctorDashboard from "@/pages/doctor/Dashboard";
import Prescriptions from "@/pages/doctor/Prescriptions";
import ReceptionDashboard from "@/pages/reception/Dashboard";
import PatientPortal from "@/pages/patient/Portal";
import BookAppointment from "@/pages/patient/Book";

function RoleProtectedRoute({ allowedRoles }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function AppRouter() {
  const location = useLocation();

  // Detect OAuth session_id in URL hash synchronously before ProtectedRoute
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<RoleBasedDashboard />} />

          <Route element={<RoleProtectedRoute allowedRoles={["admin", "doctor", "receptionist", "staff"]} />}>
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/patients" element={<Patients />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["admin", "doctor", "lab_technician"]} />}>
            <Route path="/lab" element={<Lab />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["admin"]} />}>
            <Route path="/doctors" element={<Doctors />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/departments" element={<Departments />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/calendar" element={<AdminCalendar />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/insurance" element={<Insurance />} />
            <Route path="/notifications" element={<Notifications />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["admin", "doctor"]} />}>
            <Route path="/schedule" element={<Schedule />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["doctor"]} />}>
            <Route path="/prescriptions" element={<Prescriptions />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["patient"]} />}>
            <Route path="/portal" element={<PatientPortal />} />
            <Route path="/book" element={<BookAppointment />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

function RoleBasedDashboard() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === "patient") return <PatientPortal />;
  if (role === "doctor") return <DoctorDashboard />;
  if (role === "receptionist") return <ReceptionDashboard />;
  if (role === "lab_technician") return <Navigate to="/lab" replace />;
  if (role === "staff") return <Navigate to="/appointments" replace />;
  return <AdminDashboard />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppRouter />
          <Toaster />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
